import {
  ADD_AUTOCOMPLETE_ERROR,
  ADD_ENTITY_FIELD,
  ADD_ENTITY_RELATION,
  ADD_RESOLVED_FIELD,
  ADD_VALIDATION_ERRORS,
  CLEAR_ENTITY_EDITOR,
  DELETE_AUTOCOMPLETE_ERROR,
  DELETE_ENTITY_FIELD,
  DELETE_ENTITY_RELATION,
  DELETE_RESOLVED_FIELD,
  DELETE_VALIDATION_ERRORS,
  EDIT_ENTITY_FIELD,
  EDIT_ENTITY_INFO,
  EDIT_ENTITY_RELATION,
  EDIT_RESOLVED_FIELD,
  LINK_RESOLVED_FIELD,
  LOAD_ENTITY,
  SET_IMPORT_MODE,
  SET_SELECTED_FIELD_SET,
  UNLINK_RESOLVED_FIELD,
} from '../actions/entity-designer.actions';
import { generateResolvedFields } from '../utils/entity-designer.utils';

const entityDesignerReducer = (state = {}, action) => {
  switch (action.type) {
    case LOAD_ENTITY: {
      return {
        ...state,
        entityModel: action.payload,
        originalEntityModel: action.payload,
        resolvedFields: generateResolvedFields(action.payload.EntityFields),
      };
    }

    case CLEAR_ENTITY_EDITOR: {
      return {};
    }

    case SET_IMPORT_MODE: {
      return {
        ...state,
        isImportMode: action.payload,
      };
    }

    case SET_SELECTED_FIELD_SET: {
      return {
        ...state,
        selectedFieldSet: action.payload,
      };
    }

    case EDIT_ENTITY_INFO: {
      return {
        ...state,
        entityModel: {
          ...state.entityModel,
          EntityInfo: {
            ...state.entityModel.EntityInfo,
            [action.payload.prop]: action.payload.value,
          },
        },
      };
    }

    case ADD_ENTITY_FIELD: {
      return {
        ...state,
        entityModel: {
          ...state.entityModel,
          EntityFields: [action.payload, ...state.entityModel.EntityFields],
        },
      };
    }

    case EDIT_ENTITY_FIELD: {
      const updatedFields = state.entityModel.EntityFields.map((field) => {
        if (field.rowId !== action.payload.rowId) {
          return field;
        }
        return {
          ...field,
          [action.payload.key]: action.payload.value,
        };
      });

      return {
        ...state,
        entityModel: {
          ...state.entityModel,
          EntityFields: updatedFields,
        },
      };
    }

    case DELETE_ENTITY_FIELD: {
      const updatedFields = state.entityModel.EntityFields.filter(
        (field) => field.rowId !== action.payload.rowId
      );

      return {
        ...state,
        entityModel: {
          ...state.entityModel,
          EntityFields: updatedFields,
        },
      };
    }

    case ADD_ENTITY_RELATION: {
      const relatedEntities = state.entityModel.EntityInfo?.RelatedEntities;
      const updatedEntitiesArray = relatedEntities?.length
        ? [action.payload, ...state.entityModel.EntityInfo.RelatedEntities]
        : [action.payload];
      return {
        ...state,
        entityModel: {
          ...state.entityModel,
          EntityInfo: {
            ...state.entityModel.EntityInfo,
            RelatedEntities: [...updatedEntitiesArray],
          },
        },
      };
    }

    case EDIT_ENTITY_RELATION: {
      const updatedRelations = state.entityModel.EntityInfo.RelatedEntities.map((relation) => {
        if (relation.rowId !== action.payload.rowId) {
          return relation;
        }
        return {
          ...relation,
          [action.payload.key]: action.payload.value,
        };
      });

      return {
        ...state,
        entityModel: {
          ...state.entityModel,
          EntityInfo: {
            ...state.entityModel.EntityInfo,
            RelatedEntities: updatedRelations,
          },
        },
      };
    }

    case DELETE_ENTITY_RELATION: {
      const updatedRelations = state.entityModel.EntityInfo.RelatedEntities.filter(
        (relation) => relation.rowId !== action.payload.rowId
      );

      return {
        ...state,
        entityModel: {
          ...state.entityModel,
          EntityInfo: {
            ...state.entityModel.EntityInfo,
            RelatedEntities: updatedRelations,
          },
        },
      };
    }

    case ADD_RESOLVED_FIELD: {
      return {
        ...state,
        resolvedFields: [action.payload, ...state.resolvedFields],
      };
    }

    case EDIT_RESOLVED_FIELD: {
      const updatedResolvedFields = state.resolvedFields.map((field) => {
        if (field.rowId !== action.payload.rowId) {
          return field;
        }
        return {
          ...field,
          [action.payload.key]: action.payload.value,
        };
      });

      return {
        ...state,
        resolvedFields: updatedResolvedFields,
      };
    }

    case DELETE_RESOLVED_FIELD: {
      const updatedResolvedFields = state.resolvedFields.filter(
        (field) => field.rowId !== action.payload.rowId
      );

      return {
        ...state,
        resolvedFields: updatedResolvedFields,
      };
    }

    case LINK_RESOLVED_FIELD: {
      const updatedFields = state.entityModel.EntityFields.map((field) => {
        if (field.rowId !== action.payload.fieldRowId) {
          return field;
        }

        return {
          ...field,
          FieldSet: action.payload.fieldSet ?? '',
          ResolverFor: action.payload.source ?? '',
        };
      });

      return {
        ...state,
        entityModel: {
          ...state.entityModel,
          EntityFields: updatedFields,
        },
      };
    }

    case UNLINK_RESOLVED_FIELD: {
      const updatedFields = state.entityModel.EntityFields.map((field) => {
        if (field.rowId !== action.payload.rowId) {
          return field;
        }

        return {
          ...field,
          FieldSet: '',
          ResolverFor: '',
        };
      });

      return {
        ...state,
        entityModel: {
          ...state.entityModel,
          EntityFields: updatedFields,
        },
      };
    }

    case ADD_VALIDATION_ERRORS: {
      return {
        ...state,
        validationErrors: action.payload,
      };
    }

    case DELETE_VALIDATION_ERRORS: {
      return {
        ...state,
        validationErrors: [],
      };
    }

    case ADD_AUTOCOMPLETE_ERROR: {
      const autocompleteErrors = state.autocompleteErrors ?? [];
      const isErrorExist = autocompleteErrors.some(
        (error) => error.instanceId === action.payload.instanceId
      );
      return {
        ...state,
        validationErrors: [],
        autocompleteErrors: isErrorExist
          ? autocompleteErrors
          : [...autocompleteErrors, action.payload],
      };
    }

    case DELETE_AUTOCOMPLETE_ERROR: {
      const autocompleteErrors = state.autocompleteErrors ?? [];
      const filteredErrors = autocompleteErrors.filter(
        (error) => error.instanceId !== action.payload.instanceId
      );
      return {
        ...state,
        validationErrors: [],
        autocompleteErrors: filteredErrors,
      };
    }

    default:
      return state;
  }
};

export default entityDesignerReducer;
